import axios from "axios";
import { store } from "src/boot/vuex";
import {
  AUTHENTICATE_ENDPOINT,
  AUTH_ROLES,
  CHECK_AUTH,
} from "src/constants";
import { ensureAuthenticated, invalidateAuthSession } from "src/utils";

const AUTH_BYPASS_PATHS = [
  AUTHENTICATE_ENDPOINT,
  CHECK_AUTH,
  "login",
  "logout",
];
let interceptorId = null;

export const isAuthRetryEligible = (error) => {
  const status = error?.response?.status;
  const data = error?.response?.data;
  const detail = data?.detail || data?.message || "";
  if (status === 401 || status === 403) {
    return true;
  }
  if (data?.status === "INVALID_SESSION") {
    return true;
  }
  if (typeof detail === "string" && detail.toLowerCase().includes("challenge expired")) {
    return true;
  }
  return false;
};

export const shouldSkipAuthRetry = (config) => {
  const url = config?.url || "";
  return AUTH_BYPASS_PATHS.some((path) => url.includes(path));
};

const isInvestorUserEndpoint = (url) =>
  /\/users\/[^/]+\/(portfolio|dashboard|records)(?:[/?#]|$)/.test(url || "");

export const isInvestorInvestmentEndpoint = (url) =>
  /\/carbon-projects\/[^/]+\/(?:investments\/prepare|batch-investments\/(?:quote|prepare)|stablecoin-allowance|stablecoin-approvals\/prepare)(?:[/?#]|$)/.test(
    url || ""
  );

export const shouldInvalidateInvestmentSession = (error) => {
  const status = error?.response?.status;
  const data = error?.response?.data;
  const detail = data?.detail || data?.message || "";
  if (status === 401 || data?.status === "INVALID_SESSION") {
    return true;
  }
  return (
    typeof detail === "string" &&
    /(?:challenge|session).*(?:expired|invalid)|(?:expired|invalid).*(?:challenge|session)/i.test(
      detail
    )
  );
};

export const resolveRetryRole = (config) => {
  const rawRole = config?.headers?.["X-Role"];
  const accountRole = store.state.currentAccount?.role;
  if (rawRole !== null && rawRole !== undefined && rawRole !== "") {
    const requestRole = Number(rawRole);
    if (accountRole !== null && accountRole !== undefined && Number(accountRole) !== requestRole) {
      return null;
    }
    return requestRole;
  }
  if (accountRole !== null && accountRole !== undefined) {
    return accountRole;
  }
  if (
    isInvestorUserEndpoint(config?.url || "") ||
    isInvestorInvestmentEndpoint(config?.url || "")
  ) {
    return AUTH_ROLES.INVESTOR;
  }
  return null;
};

export const registerAuthInterceptor = () => {
  if (interceptorId !== null) {
    return interceptorId;
  }
  interceptorId = axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const config = error?.config;
      if (!config || shouldSkipAuthRetry(config)) {
        return Promise.reject(error);
      }
      if (!isAuthRetryEligible(error)) {
        return Promise.reject(error);
      }

      if (isInvestorInvestmentEndpoint(config.url)) {
        if (shouldInvalidateInvestmentSession(error)) {
          invalidateAuthSession();
        }
        return Promise.reject(error);
      }

      if (config.__authRetry) {
        return Promise.reject(error);
      }

      config.__authRetry = true;
      const role = resolveRetryRole(config);
      if (role === null || role === undefined || Number.isNaN(Number(role))) {
        return Promise.reject(error);
      }
      const ok = await ensureAuthenticated({ role, force: true });
      if (!ok) {
        return Promise.reject(error);
      }

      // Cookie handles auth on retry automatically
      return axios(config);
    }
  );
  return interceptorId;
};

export const resetAuthInterceptorForTests = () => {
  interceptorId = null;
};
